import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { compact, get } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';

import { isRoleRevelioAdmin, useGetLoggedInUser } from '@revelio/auth';
import {
  type Client,
  type CreateClientRewriteMutationVariables,
  CustomTaxonomyEnum,
  MetadataKey,
  Role,
  Tab,
} from '@revelio/data-access';
import { Button as UiButton } from '@revelio/ui';

import { CreateClientMutation } from './admin-user-edit/admin-user-client-group-select';
import ClientForm, {
  type ClientFormValues,
  type ReportSelectOption,
} from './client-form';
import {
  DataBuilderEditor,
  OPEN_DATA_BUILDER_CONFIG_PARAM,
} from './data-builder-settings/data-builder-editor';
import { serializeRequestsPerSecond } from './requests-per-second-modal';
import UserForm, { type UserFormValues } from './user-form';
import { ClientDetailsQuery } from './userOperations';
import {
  getReportOptions,
  serializeOptions,
  USER_ROLE_OPTION,
  useAllClientUsersQuery,
  useSubmitUser,
} from './utils/helpers';

interface AdminClientAddProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminClientAdd = ({ isOpen, onClose }: AdminClientAddProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [postCreateClientName, setPostCreateClientName] = useState<
    string | null
  >(null);
  const {
    isOpen: isPostCreateModalOpen,
    onOpen: onPostCreateModalOpen,
    onClose: onPostCreateModalClose,
  } = useDisclosure();

  const toast = useToast();

  const {
    control: clientControl,
    register,
    formState: { errors },
    handleSubmit,
    watch: clientWatch,
  } = useForm<ClientFormValues>();

  const userFormMethods = useForm<UserFormValues>({
    defaultValues: { role: USER_ROLE_OPTION },
  });
  const { loggedInUser } = useGetLoggedInUser();
  const isClientAdmin = loggedInUser.role === Role.ClientAdmin;

  const [, createClientGroup] = useMutation(CreateClientMutation);

  const hasReportsTab = clientWatch('tabs', [])?.some(
    (tab) => tab?.value === 'reports'
  );

  const [{ fetching: fetchingClient, data: clientData }] = useQuery({
    query: ClientDetailsQuery,
    variables: { ids: [loggedInUser?.client_name as string] },
    pause: !loggedInUser?.client_name,
  });

  const [reportOptions, setReportOptions] = useState<ReportSelectOption[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const client = get(clientData, 'clients[0]');

    if (!fetchingClient && client) {
      const allReportTypes = getReportOptions(client);

      setReportOptions(allReportTypes);
    }
  }, [clientData, hasReportsTab, fetchingClient]);

  const { addUser } = useSubmitUser({
    loggedInUser,
    isClientAdmin,
    onClose,
  });

  const [clientName, setClientName] = useState<string>('');
  const { refetch } = useAllClientUsersQuery(clientName);
  const goToEditClient = () => {
    if (!postCreateClientName) {
      onPostCreateModalClose();
      return;
    }
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('editing', 'client');
    newSearchParams.set('client', postCreateClientName);
    newSearchParams.set(OPEN_DATA_BUILDER_CONFIG_PARAM, 'true');
    setSearchParams(newSearchParams);
    onPostCreateModalClose();
  };

  const onSubmit = handleSubmit(async (formData) => {
    const reportTypesToUse = formData.report_types
      ? serializeOptions(formData.report_types).reportTypes
      : [];
    const selectedTabs = compact(formData.tabs.map((tab) => tab.value));
    const hasDeliverablesTab = selectedTabs.includes(Tab.Deliverables);

    const isRevelioAdmin = isRoleRevelioAdmin(loggedInUser.role);
    const apiMetadata = [
      {
        key: MetadataKey.RequestsPerSecond,
        value: serializeRequestsPerSecond(formData.requests_per_second),
      },
      {
        key: MetadataKey.RequestsPerMonth,
        value: formData.requests_per_month,
      },
    ].filter(
      (metadataValue) =>
        metadataValue.value !== undefined &&
        metadataValue.value !== null &&
        metadataValue.value !== ''
    );

    const formatData: CreateClientRewriteMutationVariables = {
      client_name: formData.client_name,
      tabs: selectedTabs,
      live: formData.live,
      active: true, // backend cannot create inactive client group. Can only be edited
      linkup_postings: formData.linkup_postings,
      num_seats: formData?.num_seats?.toString(),
      custom_taxonomy:
        formData.custom_taxonomy?.value || CustomTaxonomyEnum.Adaptive,
      ...(isRevelioAdmin && apiMetadata.length > 0
        ? { metadata: apiMetadata }
        : {}),
      ...(hasReportsTab && reportTypesToUse
        ? { reports_configuration: { report_types: reportTypesToUse } }
        : {}),
    };

    const result = await createClientGroup(formatData);
    if (result?.data?.createClientGroup && !result.error) {
      const newClientName = result.data?.createClientGroup?.client_name;
      const isEditingUser = searchParams.get('editing') === 'user';
      if (!isEditingUser) {
        const {
          company_lists: _company_lists,
          tabs: _tabs,
          ...updatedFormatData
        } = formatData;
        const newClient = {
          ...updatedFormatData,
          ...(newClientName ? { client_name: newClientName } : {}),
        };
        const newSearchParams = new URLSearchParams(searchParams);

        await userFormMethods.handleSubmit(async (formData) => {
          if (newClientName) {
            await addUser({
              formData,
              specifiedClient: newClient as unknown as Client,
            });

            setClientName(newClientName);
            refetch();
          }
        })();

        newSearchParams.set('client', newClientName ?? '');

        setSearchParams(newSearchParams);
      }
      onClose();
      // Don't interrupt the user-edit flow: Configure Now navigates to
      // editing=client, which would abandon the in-progress user form.
      if (!isEditingUser && hasDeliverablesTab && newClientName) {
        setPostCreateClientName(newClientName);
        onPostCreateModalOpen();
      }
    } else {
      toast({
        title: 'Error',
        description: 'An error occurred',
        status: 'error',
        duration: 4000,
        position: 'top-right',
      });
    }
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        isCentered
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent minWidth="65%" maxHeight="95vh" margin="5">
          <ModalHeader>Add a new client</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <FormControl id="name" isInvalid={!!errors.client_name}>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Client Name
              </FormLabel>
              <Input
                {...register('client_name', {
                  required: 'Name is required.',
                  minLength: {
                    value: 1,
                    message: 'Minimum length should be 1',
                  },
                })}
              />
              <FormErrorMessage>
                {errors.client_name && errors.client_name.message}
              </FormErrorMessage>
            </FormControl>
            <ClientForm
              clientToEdit={null}
              control={clientControl}
              register={register}
              watch={clientWatch}
              errors={errors}
              reportOptions={hasReportsTab ? reportOptions : []}
              renderDataBuilderConfig={() => <DataBuilderEditor.AddFlow />}
            />
            <Text
              fontSize={20}
              fontWeight={600}
              color="blackAlpha.900"
              mt={6}
              mb={4}
            >
              Add a new user
            </Text>
            <UserForm
              useFormMethods={userFormMethods}
              userToEdit={null}
              setPassword={(newPassword: string) =>
                userFormMethods.setValue('password', newPassword)
              }
              hideChangeClientGroup
            />
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button onClick={onSubmit} colorScheme="green">
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isPostCreateModalOpen}
        onClose={onPostCreateModalClose}
        size="md"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary">
            Data Builder Needs Configuration
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color="text.primary">
              This client was created successfully and their Data Builder
              permissions can now be configured.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <UiButton
              variant="ghost"
              onClick={onPostCreateModalClose}
              color="secondary"
            >
              Leave Empty
            </UiButton>
            <UiButton onClick={goToEditClient} color="primary">
              Configure Now
            </UiButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminClientAdd;
